import React, { useEffect, useRef, useState } from "react";
import {
  CreateWeddingPayload,
  DRESS_CODE_LABELS,
  DressCode,
  ListingStatus,
  VIBE_TAG_LABELS,
  VibeTag,
} from "../types";

type FieldErrors = {
  title?: string;
  date?: string;
  city?: string;
  state?: string;
};

interface Props {
  onSubmit: (data: CreateWeddingPayload) => Promise<void>;
  loading?: boolean;
  initial?: Partial<CreateWeddingPayload>;
  submitError?: string;
  submitSuccess?: string;
  onClearSubmitFeedback?: () => void;
}

const DRESS_CODES = Object.keys(DRESS_CODE_LABELS) as DressCode[];
const VIBE_TAGS = Object.keys(VIBE_TAG_LABELS) as VibeTag[];

const STRANGER_SEATS_COPY =
  "Your wedding appears on the Discover page, where people you have not invited can find it and request a seat. You approve or decline every request.";

const STRANGER_SEATS_INFO = `Your wedding page can always be found. Anyone who searches your names can see your names, date, and city, and can RSVP if you have invited them. That is how guests who lost their invitation find you.

This checkbox is about strangers. Leave it unchecked and only people you have invited can attend. Check it and your wedding also appears on the Discover page, where people you do not know can request a seat. You approve or decline each one, and no one is charged until you approve.`;

const toDatetimeLocal = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const toDateInput = (value?: string) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const statusLabel = (status?: ListingStatus | string) => {
  if (!status) return "live";
  return String(status).replace("_", " ");
};

const inferVenueAddress = (initial?: Partial<CreateWeddingPayload>) => {
  if (!initial) return "";
  if (initial.venue_address) return initial.venue_address;
  if (!initial.location) return "";
  const constructed = [initial.venue_name, initial.city, initial.state]
    .filter(Boolean)
    .join(", ");
  if (constructed && initial.location.trim() === constructed) return "";
  return initial.location;
};

const InfoTip: React.FC<{ label: string; text: string }> = ({ label, text }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className={`info-tip${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="info-tip-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        i
      </button>
      <span className="info-tip-panel" role="tooltip">
        {text}
      </span>
    </span>
  );
};

const WeddingForm: React.FC<Props> = ({
  onSubmit,
  loading,
  initial,
  submitError,
  submitSuccess,
  onClearSubmitFeedback,
}) => {
  const isEdit = Boolean(initial);
  const allowStrangersDefault =
    Boolean(initial?.allow_ghost_seats) || Boolean(initial?.is_public);
  const [form, setForm] = useState<CreateWeddingPayload>({
    title: initial?.title ?? "",
    date: toDatetimeLocal(initial?.date),
    description: initial?.description ?? "",
    couple_name_1: initial?.couple_name_1 ?? "",
    couple_name_2: initial?.couple_name_2 ?? "",
    story: initial?.story ?? "",
    venue_name: initial?.venue_name ?? "",
    venue_address: inferVenueAddress(initial),
    city: initial?.city ?? "",
    state: initial?.state ?? "",
    zip: initial?.zip ?? "",
    dress_code: initial?.dress_code ?? "",
    vibe_tags: initial?.vibe_tags ?? [],
    sale_close_date: toDateInput(initial?.sale_close_date),
    listing_status: initial?.listing_status ?? "live",
    mark_sold_out: initial?.listing_status === "sold_out",
    requires_21_plus: initial?.requires_21_plus ?? false,
    is_public: allowStrangersDefault,
    allow_ghost_seats: allowStrangersDefault,
    ticket_price: initial?.ticket_price ?? 0,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    onClearSubmitFeedback?.();
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "ticket_price"
            ? Math.round(parseFloat(value || "0") * 100)
            : value,
    }));
    if (name === "title" || name === "date" || name === "city" || name === "state") {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const toggleVibeTag = (tag: VibeTag) => {
    onClearSubmitFeedback?.();
    setForm((prev) => {
      const current = prev.vibe_tags ?? [];
      const next = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag];
      return { ...prev, vibe_tags: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = (form.title || "").trim();
    const date = (form.date || "").trim();
    const city = (form.city || "").trim();
    const state = (form.state || "").trim();
    const nextErrors: FieldErrors = {};
    if (!title) nextErrors.title = "Wedding title is required.";
    if (!date) nextErrors.date = "Date is required.";
    if (!city) nextErrors.city = "City is required.";
    if (!state) nextErrors.state = "State is required.";
    setFieldErrors(nextErrors);
    const blockingErrors = Object.values(nextErrors).filter(Boolean);
    if (blockingErrors.length) {
      onClearSubmitFeedback?.();
      return;
    }

    const allowStrangers = Boolean(form.allow_ghost_seats);
    const {
      listing_status: _ignored,
      rsvp_mode: _rsvpMode,
      location: _location,
      ...rest
    } = form;
    const payload: CreateWeddingPayload = {
      ...rest,
      city,
      state,
      zip: (form.zip || "").trim() || undefined,
      venue_name: (form.venue_name || "").trim() || undefined,
      venue_address: (form.venue_address || "").trim() || undefined,
      allow_ghost_seats: allowStrangers,
      is_public: allowStrangers,
      dress_code: form.dress_code || undefined,
      sale_close_date: form.sale_close_date || undefined,
    };
    if (isEdit) {
      payload.mark_sold_out = Boolean(form.mark_sold_out);
    }
    await onSubmit(payload);
  };

  const currentStatus = form.listing_status || "live";
  const isPast = currentStatus === "past";

  const validationMessages = Object.values(fieldErrors).filter(Boolean);
  const statusMessage = validationMessages.length
    ? `Can't save yet: ${validationMessages.join(" ")}`
    : submitError
      ? submitError
      : submitSuccess || "";
  const statusKind = validationMessages.length || submitError ? "error" : submitSuccess ? "success" : "";

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="couple_name_1">Partner 1</label>
          <input
            id="couple_name_1"
            name="couple_name_1"
            value={form.couple_name_1 || ""}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="couple_name_2">Partner 2</label>
          <input
            id="couple_name_2"
            name="couple_name_2"
            value={form.couple_name_2 || ""}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="title">Wedding Title *</label>
        <input
          id="title"
          name="title"
          value={form.title}
          onChange={handleChange}
          required
          aria-invalid={Boolean(fieldErrors.title)}
          aria-describedby={fieldErrors.title ? "title-error" : undefined}
        />
        {fieldErrors.title && (
          <p className="field-error" id="title-error">
            {fieldErrors.title}
          </p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="date">Date *</label>
        <input
          id="date"
          name="date"
          type="datetime-local"
          value={form.date}
          onChange={handleChange}
          required
          aria-invalid={Boolean(fieldErrors.date)}
          aria-describedby={fieldErrors.date ? "date-error" : undefined}
        />
        {fieldErrors.date && (
          <p className="field-error" id="date-error">
            {fieldErrors.date}
          </p>
        )}
        {isEdit && isPast && (
          <p className="form-hint">
            This listing is past. Moving the date into the future will set it live again.
          </p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="story">Your Story</label>
        <textarea
          id="story"
          name="story"
          value={form.story || ""}
          onChange={handleChange}
          rows={4}
          placeholder="Share a short story about your relationship or celebration..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={form.description || ""}
          onChange={handleChange}
          rows={3}
          placeholder="Tell guests about your special day..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="venue_name">Name of Venue</label>
        <input
          id="venue_name"
          name="venue_name"
          value={form.venue_name || ""}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="venue_address">Address</label>
        <input
          id="venue_address"
          name="venue_address"
          value={form.venue_address || ""}
          onChange={handleChange}
          autoComplete="street-address"
        />
      </div>

      <div className="form-row form-row-address">
        <div className="form-group">
          <label htmlFor="city">City *</label>
          <input
            id="city"
            name="city"
            value={form.city || ""}
            onChange={handleChange}
            autoComplete="address-level2"
            aria-invalid={Boolean(fieldErrors.city)}
            aria-describedby={fieldErrors.city ? "city-error" : undefined}
          />
          {fieldErrors.city && (
            <p className="field-error" id="city-error">
              {fieldErrors.city}
            </p>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="state">State *</label>
          <input
            id="state"
            name="state"
            value={form.state || ""}
            onChange={handleChange}
            maxLength={2}
            autoComplete="address-level1"
            aria-invalid={Boolean(fieldErrors.state)}
            aria-describedby={fieldErrors.state ? "state-error" : undefined}
          />
          {fieldErrors.state && (
            <p className="field-error" id="state-error">
              {fieldErrors.state}
            </p>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="zip">Zip</label>
          <input
            id="zip"
            name="zip"
            value={form.zip || ""}
            onChange={handleChange}
            autoComplete="postal-code"
            inputMode="numeric"
            maxLength={10}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="dress_code">Dress Code</label>
        <select
          id="dress_code"
          name="dress_code"
          value={form.dress_code || ""}
          onChange={handleChange}
        >
          <option value="">Select dress code</option>
          {DRESS_CODES.map((code) => (
            <option key={code} value={code}>
              {DRESS_CODE_LABELS[code]}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Vibe Tags</label>
        <div className="checkbox-group" style={{ display: "grid", gap: "0.5rem" }}>
          {VIBE_TAGS.map((tag) => (
            <label key={tag}>
              <input
                type="checkbox"
                checked={(form.vibe_tags || []).includes(tag)}
                onChange={() => toggleVibeTag(tag)}
              />
              {VIBE_TAG_LABELS[tag]}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="sale_close_date">Sale Close Date</label>
        <input
          id="sale_close_date"
          name="sale_close_date"
          type="date"
          value={form.sale_close_date || ""}
          onChange={handleChange}
        />
      </div>

      {isEdit && (
        <div className="form-group">
          <p className="form-hint" style={{ marginBottom: "0.75rem" }}>
            Listing status: <strong>{statusLabel(currentStatus)}</strong>
            {" — "}
            new weddings go live automatically; after the wedding date they become expired and are no longer visible on the discover page.
          </p>
          {!isPast && (
            <div className="checkbox-group">
              <label>
                <input
                  name="mark_sold_out"
                  type="checkbox"
                  checked={Boolean(form.mark_sold_out)}
                  onChange={(e) => {
                    onClearSubmitFeedback?.();
                    setForm((prev) => ({ ...prev, mark_sold_out: e.target.checked }));
                  }}
                />
                Mark as sold out
              </label>
              <p className="form-hint">
                Sold-out listings stay on Discover but signal that seats are gone. Uncheck to
                reopen while the wedding date is still upcoming.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="ticket_price">Legacy Ticket Price (USD)</label>
        <input
          id="ticket_price"
          name="ticket_price"
          type="number"
          min="0"
          step="0.01"
          value={(form.ticket_price || 0) / 100}
          onChange={handleChange}
        />
        <p className="form-hint">
          Fallback price used when tier pricing is not configured.
        </p>
      </div>

      <div className="form-group checkbox-group">
        <label>
          <input
            name="requires_21_plus"
            type="checkbox"
            checked={Boolean(form.requires_21_plus)}
            onChange={handleChange}
          />
          Requires 21+
        </label>
      </div>

      <div className="form-group">
        <div className="checkbox-with-info">
          <label>
            <input
              name="allow_ghost_seats"
              type="checkbox"
              checked={Boolean(form.allow_ghost_seats)}
              onChange={(e) => {
                const checked = e.target.checked;
                onClearSubmitFeedback?.();
                setForm((prev) => ({
                  ...prev,
                  allow_ghost_seats: checked,
                  is_public: checked,
                }));
              }}
            />
            Allow strangers to request seats
          </label>
          <InfoTip
            label="About allowing strangers to request seats"
            text={STRANGER_SEATS_INFO}
          />
        </div>
        <p className="form-hint">{STRANGER_SEATS_COPY}</p>
      </div>

      <div className="form-submit-row">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading
            ? isEdit
              ? "Saving..."
              : "Creating..."
            : isEdit
              ? "Save Changes"
              : "Create Wedding"}
        </button>
        {statusMessage && (
          <p
            className={`form-submit-status${statusKind ? ` is-${statusKind}` : ""}`}
            role={statusKind === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            {statusMessage}
          </p>
        )}
      </div>
    </form>
  );
};

export default WeddingForm;
